import {getLogger} from '../../../../shared/utils/logUtils'
import {isSessionExpiredByPolicy} from '../../../../shared/utils/sessionExpirationPreference'
import {tryRefreshCognitoSession} from '../../../../shared/utils/aws/helpers'

// Global flag to prevent multiple dialogs
let isShowingAuthDialog = false

// Global function to show dialog (will be set by the provider)
let globalShowDialog: (() => void) | null = null
// Global function to handle token expiration (will be set by the auth context)
let globalHandleTokenExpiration: (() => void) | null = null

// Set the global show dialog function
export const setGlobalShowDialog = (showDialog: () => void) => {
  globalShowDialog = showDialog
}

// Set the global token expiration handler
export const setGlobalTokenExpirationHandler = (handler: () => void) => {
  globalHandleTokenExpiration = handler
}

// Get the global token expiration handler
export const getGlobalTokenExpirationHandler = () => globalHandleTokenExpiration

/**
 * When the profile session-duration policy has fired, we intentionally end the login;
 * do not silently extend Cognito tokens past that window.
 */
export async function refreshSessionIfPolicyAllows(): Promise<boolean> {
  if (isSessionExpiredByPolicy()) {
    return false
  }
  return tryRefreshCognitoSession()
}

// Custom error handler for authentication failures
export const handleAuthError = (error: any) => {
  const logger = getLogger()

  // Check if it's an authentication error.
  // Rely on 401 for re-login. Many app Lambdas return 403 for *authorization* failures
  // (forbidden resource, throttling, etc.) which must not show the "session expired" flow.
  const isAuthError =
    error?.response?.status === 401 ||
    error?.message?.includes('Token expired') ||
    error?.message?.includes('Not authorized') ||
    error?.message?.includes('No current user') ||
    error?.message?.includes('User is not authenticated') ||
    error?.message?.includes('The user is not authenticated') ||
    error?.code === 'NotAuthorizedException' ||
    error?.code === 'TokenExpiredException' ||
    error?.name === 'NoUserPoolException' ||
    error?.name === 'NotAuthorizedException' ||
    // Handle string errors
    (typeof error === 'string' &&
      (error.includes('No current user') ||
        error.includes('Token expired') ||
        error.includes('Not authorized') ||
        error.includes('User is not authenticated') ||
        error.includes('The user is not authenticated')))

  if (isAuthError && !isShowingAuthDialog) {
    logger.warn('Authentication error detected:', error)

    // Show the dialog first, don't clear auth state yet
    if (globalShowDialog) {
      isShowingAuthDialog = true
      globalShowDialog()
    }
  }
}

// Wrapper for API calls to handle auth errors
export const apiCallWithAuthCheck = async <T>(apiCall: () => Promise<T>): Promise<T> => {
  try {
    return await apiCall()
  } catch (error) {
    const status = (error as {response?: {status?: number}})?.response?.status
    if (status === 401 && !isSessionExpiredByPolicy()) {
      const refreshed = await refreshSessionIfPolicyAllows()
      if (refreshed) {
        try {
          return await apiCall()
        } catch (retryError) {
          handleAuthError(retryError)
          throw retryError
        }
      }
    }
    handleAuthError(error)
    throw error
  }
}

// Reset the dialog flag (called when dialog is closed)
export const resetAuthDialogFlag = () => {
  isShowingAuthDialog = false
}

// Check if user is currently authenticated
export const isUserAuthenticated = (): boolean => {
  try {
    const cognitoUser = localStorage.getItem('cognito-user')
    return !!cognitoUser
  } catch (error) {
    return false
  }
}
